/**
 * Data loading pipeline: ONE Storefront API metafields query → parsed,
 * frozen client datasets. Session-cached (5 min) so repeat page views don't
 * re-fetch; see ./cache.ts.
 */
import { cacheGet, cacheRemove, cacheSet, DEFAULT_TTL_MS } from './cache'
import {
  fetchShopMetafields,
  parsePreorderLimitsValue,
  parseSellingPlansValue,
  parseSettingsValue,
  parseShippingTextsValue,
  pickMetafieldValue,
  type RawShopMetafields,
  type StorefrontQueryContext,
} from './metafields'
import type {
  SellingPlan,
  StoqSettings,
  VariantPreorderLimits,
  VariantShippingTexts,
} from './types'
import { warn } from './util'

export interface ClientData {
  settings: Readonly<StoqSettings>
  sellingPlans: ReadonlyArray<SellingPlan>
  disabledPlanIds: number[]
  preorderLimits: VariantPreorderLimits
  shippingTexts: VariantShippingTexts
}

export interface LoadContext {
  /** Cache identity (shop + market). */
  shop: string
  marketId?: number
  locale?: string
  storefront: StorefrontQueryContext
}

function cacheKey(ctx: LoadContext): string {
  const { namespace, apiVersion } = ctx.storefront
  return ['stoq:sdk:v1', ctx.shop, ctx.marketId ?? '-', 'storefront_metafields', namespace, apiVersion]
    .join(':')
}

function freezePlans(plans: SellingPlan[]): ReadonlyArray<SellingPlan> {
  for (const plan of plans) {
    if (Array.isArray(plan.variant_ids)) Object.freeze(plan.variant_ids)
    if (Array.isArray(plan.shopify_market_ids)) Object.freeze(plan.shopify_market_ids)
    Object.freeze(plan)
  }
  return Object.freeze(plans)
}

/**
 * Parses the raw metafield values into client datasets. `settings` is
 * required — everything else degrades to defaults with a console warning
 * (only when the shop's configuration suggests the dataset should exist).
 */
function parseDatasets(raw: RawShopMetafields, locale: string | undefined, namespace: string): ClientData {
  const settings = parseSettingsValue(pickMetafieldValue(raw, 'settings'), locale)
  if (settings === undefined) {
    throw new Error(
      `[Stoq] the "settings" shop metafield (namespace "${namespace}") is missing or `
        + 'invalid — verify the namespace matches the STOQ environment and that the app has '
        + 'synced its metafields for this shop',
    )
  }

  // `cachedSellingPlans` carries the full payload (incl. disabled_plan_ids);
  // the bare `selling_plans` array is the fallback for shops whose
  // metafields haven't been re-synced since it was introduced.
  const plansValue = pickMetafieldValue(raw, 'cachedSellingPlans') ?? pickMetafieldValue(raw, 'sellingPlans')
  const plansResponse = parseSellingPlansValue(plansValue)
  if (plansResponse === undefined && plansValue !== null) {
    warn('failed to load selling plans; continuing with defaults')
  }

  const plans = plansResponse?.plans ?? []
  const hasStoqPlan = settings.preorder_enabled === true
    && plans.some((plan) => plan.enabled && plan.inventory_provider === 'stoq')

  const dataset = <T>(parsed: T | undefined, fallback: T, label: string, expected: boolean): T => {
    if (parsed !== undefined) return parsed
    if (expected) warn(`failed to load ${label}; continuing with defaults`)
    return fallback
  }

  return {
    settings: Object.freeze({ ...settings }),
    sellingPlans: freezePlans(plans),
    disabledPlanIds: Array.isArray(plansResponse?.disabled_plan_ids)
      ? plansResponse.disabled_plan_ids
      : [],
    preorderLimits: dataset(
      parsePreorderLimitsValue(pickMetafieldValue(raw, 'variantPreorderLimits')),
      {},
      'variant preorder limits',
      hasStoqPlan,
    ),
    shippingTexts: dataset(
      parseShippingTextsValue(pickMetafieldValue(raw, 'variantShippingTexts')),
      {},
      'variant shipping texts',
      hasStoqPlan,
    ),
  }
}

/**
 * Loads (or re-loads with `force`) all client datasets. The RAW metafield
 * values are cached — not the parsed datasets — so the locale overlay is
 * applied on every load and the cache stays locale-independent.
 */
export async function loadClientData(ctx: LoadContext, force: boolean): Promise<ClientData> {
  const key = cacheKey(ctx)
  if (force) cacheRemove(key)

  let raw = force ? null : cacheGet<RawShopMetafields>(key)
  if (raw === null) {
    raw = await fetchShopMetafields(ctx.storefront)
    cacheSet(key, raw, DEFAULT_TTL_MS)
  }

  return parseDatasets(raw, ctx.locale, ctx.storefront.namespace)
}
